import React, { useEffect, useRef, useState } from "react"
import { useTranslation } from 'react-i18next'

import { GET, PUT, httpOk } from 'helpers/api'
import HintIcon from 'pages/parts/HintIcon'

/**
 * Бронь столов точки — уходит отдельным запросом (PUT /reservation-settings):
 * PUT /pos перезаписывает точку целиком, а эти поля читают касса и сервисы бронирования.
 * Своей кнопки нет: сохраняет общая «Сохранить» карточки через saveRef — со своей кнопкой
 * владельцы жали общую, и настройки молча не сохранялись.
 *  - длительность брони по умолчанию (бронь без времени окончания);
 *  - через сколько минут после начала неподтверждённая посадкой бронь — «не пришёл»;
 *  - лимит гостей в час для автоподтверждения броней без стола от сервисов бронирования
 *    (пусто — каждая такая бронь приходит заявкой и ждёт кассира).
 */
function ReservationSettings({ posId, saveRef }) {
	const { t } = useTranslation()
	// Трогали ли поля: без правок общая кнопка запрос не шлёт
	const changed = useRef(false)

	const [data, setData] = useState({
		'reservation_minutes': 60,
		'reservation_no_show_minutes': 0,
		'reservation_unassigned_guests_per_hour': '',
	})

	async function getData() {
		const response = await GET('/reservation-settings', { 'pos_id': posId }, { loader: true })
		if (httpOk(response)) {
			setData({
				...response.data,
				'reservation_unassigned_guests_per_hour': response.data.reservation_unassigned_guests_per_hour ?? '',
			})
		}
	}

	// true — можно идти дальше; ошибку валидации покажет тост api.js
	async function save() {
		if (!changed.current) return true
		const response = await PUT('/reservation-settings', { ...data, 'pos_id': posId }, { loader: true, notification: false })
		if (!httpOk(response)) return false
		changed.current = false
		return true
	}

	function setField(field, value) {
		changed.current = true
		setData({ ...data, [field]: value })
	}

	if (saveRef) saveRef.current = save

	function onlyDigits(value) {
		return String(value).replace(/\D/g, '').slice(0, 5)
	}

	useEffect(() => {
		if (posId) getData()
	}, [posId]) // eslint-disable-line react-hooks/exhaustive-deps

	if (!posId) return null

	return (
		<div>
			<div className="form-group">
				<label>{t('reservation_default_minutes')}<HintIcon text={t('reservation_default_minutes_hint')} /></label>
				<input type="text" inputMode="numeric" className="form-control"
					value={data.reservation_minutes ?? ''}
					onChange={(e) => setField('reservation_minutes', onlyDigits(e.target.value))} />
			</div>

			<div className="form-group">
				<label>{t('reservation_no_show_minutes')}<HintIcon text={t('reservation_no_show_minutes_hint')} /></label>
				<input type="text" inputMode="numeric" className="form-control"
					value={data.reservation_no_show_minutes ?? ''}
					onChange={(e) => setField('reservation_no_show_minutes', onlyDigits(e.target.value))} />
			</div>

			<div className="form-group">
				<label>{t('reservation_unassigned_limit')}<HintIcon text={t('reservation_unassigned_limit_hint')} /></label>
				<input type="text" inputMode="numeric" className="form-control"
					placeholder={t('reservation_unassigned_limit_empty')}
					value={data.reservation_unassigned_guests_per_hour ?? ''}
					onChange={(e) => setField('reservation_unassigned_guests_per_hour', onlyDigits(e.target.value))} />
			</div>
		</div>
	)
}

export default ReservationSettings
